/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";

import {
  Copy,
  Download,
  Loader2,
  Mail,
  PlayCircle,
  Plus,
  Upload,
} from "lucide-react";

import { useState } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import MailboxWizard from "@/components/popups/add-mailbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { usePermission } from "@/hooks/use-premission";
import { useServices } from "@/services/hooks/useServices";
import { saveAs } from "file-saver";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/utils";
import { AwsSesWizard } from "./add-mailbox-dialogs/aws-stmp";
import { AmazonApiDialog } from "./add-mailbox-dialogs/aws-api";
import { SendGridWizard } from "./add-mailbox-dialogs/sendgrid-smtp";
import { SendGridApiDialog } from "./add-mailbox-dialogs/sendgrid-api";
import { AzureSmtpWizard } from "./add-mailbox-dialogs/azure-smtp";

type AddMailboxDialogType =
  | "google"
  | "microsoft"
  | "google_bulk"
  | "google_workspace_smtp"
  | "imap"
  | "aws_smtp"
  | "aws_api"
  | "sendgrid_smtp"
  | "sendgrid_api"
  | "azus_smtp";

export default function MailboxMainActions() {
  const [activeDialog, setActiveDialog] = useState<
    AddMailboxDialogType | "provider" | null
  >(null);
  const [exporting, setExporting] = useState(false);

  const { mailboxService } = useServices();

  const { hasPermission } = usePermission();

  const handleSelectProvider = async (provider: AddMailboxDialogType) => {
    setActiveDialog(provider);
  };

  const handleExportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // Trigger client-side export using the Ag Grid instance on the page
      window.dispatchEvent(new Event("mailboxes-export-csv"));
      window.cioanalytics?.track("mailboxes_exported", {});
    } catch (error) {
      const errorMessage = extractErrorMessage(error, "Failed to export CSV");
      toast.error(errorMessage);
    } finally {
      // slight delay so users can perceive feedback and to avoid rapid re-clicks
      setTimeout(() => setExporting(false), 800);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="w-full sm:w-auto" size="sm">
            <Upload className="h-4 w-4" />
            <span className="">
              {hasPermission("mailboxes:add") ? "Import/Export" : "Export"}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {hasPermission("mailboxes:add") && (
            <DropdownMenuItem asChild>
              <Link to="/mailboxes/import">
                <Upload className="h-4 w-4 shrink-0" />
                Import Mailboxes
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={handleExportCsv} disabled={exporting}>
            {exporting ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            ) : (
              <Download className="h-4 w-4 shrink-0" />
            )}
            {exporting ? "Exporting..." : "Export Mailboxes"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {hasPermission("mailboxes:add") && (
        <Button
          onClick={() => setActiveDialog("provider")}
          className="w-full sm:w-auto"
          size="sm"
          aria-label="basic-tour-guide"
          id="add-mailbox"
        >
          <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
          <span className="hidden sm:inline">Add New Mailbox</span>
          <span className="sm:hidden">Add Mailbox</span>
        </Button>
      )}

      {/* Render the SelectProviderDialog */}
      {activeDialog === "provider" && (
        <SelectProviderDialog
          open={activeDialog === "provider"}
          onOpenChange={(open) => setActiveDialog(open ? "provider" : null)}
          onSelectProvider={handleSelectProvider}
        />
      )}

      {/* Render the GoogleDwdInstructionsDialog */}
      {activeDialog === "google" && (
        <GoogleDwdInstructionsDialog
          open={activeDialog === "google"}
          onOpenChange={(open) => setActiveDialog(open ? "google" : null)}
        />
      )}

      {/* Render the MicrosoftConsentInstructionsDialog */}
      {activeDialog === "microsoft" && (
        <MicrosoftConsentInstructionsDialog
          open={activeDialog === "microsoft"}
          onOpenChange={(open) => setActiveDialog(open ? "microsoft" : null)}
        />
      )}

      {/* Render the GoogleWorkspaceBulkDialog */}
      {activeDialog === "google_bulk" && (
        <GoogleWorkspaceBulkDialog
          open={activeDialog === "google_bulk"}
          onOpenChange={(open) => setActiveDialog(open ? "google_bulk" : null)}
        />
      )}

      {activeDialog === "imap" && (
        <MailboxWizard
          setShowAddMailboxDialog={(show) =>
            setActiveDialog(show ? "imap" : null)
          }
          showAddMailboxDialog={activeDialog === "imap"}
        />
      )}

      {activeDialog === "google_workspace_smtp" && (
        <MailboxWizard
          variant="google_workspace_smtp"
          setShowAddMailboxDialog={(show) =>
            setActiveDialog(show ? "google_workspace_smtp" : null)
          }
          showAddMailboxDialog={activeDialog === "google_workspace_smtp"}
        />
      )}

      {activeDialog === "aws_smtp" && (
        <AwsSesWizard
          open={activeDialog === "aws_smtp"}
          onOpenChange={(open) => setActiveDialog(open ? "aws_smtp" : null)}
        />
      )}

      {activeDialog === "aws_api" && (
        <AmazonApiDialog
          open={activeDialog === "aws_api"}
          onOpenChange={(open) => setActiveDialog(open ? "aws_api" : null)}
        />
      )}

      {activeDialog === "sendgrid_smtp" && (
        <SendGridWizard
          open={activeDialog === "sendgrid_smtp"}
          onOpenChange={(open) =>
            setActiveDialog(open ? "sendgrid_smtp" : null)
          }
        />
      )}

      {activeDialog === "sendgrid_api" && (
        <SendGridApiDialog
          open={activeDialog === "sendgrid_api"}
          onOpenChange={(open) => setActiveDialog(open ? "sendgrid_api" : null)}
        />
      )}

      {activeDialog === "azus_smtp" && (
        <AzureSmtpWizard
          open={activeDialog === "azus_smtp"}
          onOpenChange={(open: boolean) =>
            setActiveDialog(open ? "azus_smtp" : null)
          }
        />
      )}
    </>
  );
}

interface SelectProviderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectProvider: (provider: AddMailboxDialogType) => void;
}

const providerOptions: {
  label: string;
  value: AddMailboxDialogType;
  icon: React.ReactNode;
}[] = [
  {
    label: "Google Workspace (DWD)",
    value: "google_bulk",
    icon: <img src="/googleIcon.png" alt="google icon" className="!size-5" />,
  },
  {
    label: "Google Workspace (App Password)",
    value: "google_workspace_smtp",
    icon: <img src="/googleIcon.png" alt="google icon" className="!size-5" />,
  },
  {
    label: "Microsoft",
    value: "microsoft",
    icon: (
      <img src="/microsoftIcon.png" alt="microsoft icon" className="!size-5" />
    ),
  },
  {
    label: "Azure SMTP",
    value: "azus_smtp",
    icon: <img src="/azure.jpeg" alt="azure icon" className="!size-5" />,
  },
  {
    label: "Private SMTP",
    value: "imap",
    icon: <Mail className="!size-5" />,
  },
  {
    label: "AWS SES SMTP",
    value: "aws_smtp",
    icon: <img src="/amazonIcon.png" alt="aws icon" className="!size-5" />,
  },
  {
    label: "AWS SES API",
    value: "aws_api",
    icon: <img src="/amazonIcon.png" alt="aws icon" className="!size-5" />,
  },
  {
    label: "SendGrid SMTP",
    value: "sendgrid_smtp",
    icon: (
      <img src="/sendGridIcon.png" alt="sendgrid icon" className="!size-5" />
    ),
  },
  {
    label: "SendGrid API",
    value: "sendgrid_api",
    icon: (
      <img src="/sendGridIcon.png" alt="sendgrid icon" className="!size-5" />
    ),
  },

];

const SelectProviderDialog = ({
  open,
  onOpenChange,
  onSelectProvider,
}: SelectProviderDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect Mailbox</DialogTitle>
          <DialogDescription>
            Select the provider for the mailbox you want to connect.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2 py-4">
          {providerOptions.map((provider) => (
            <Button
              key={provider.value}
              variant="outline"
              className="justify-start gap-3 h-12"
              onClick={() => onSelectProvider(provider.value)}
            >
              {provider.icon}
              <span className="font-medium">{provider.label}</span>
            </Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface GoogleDwdInstructionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const GoogleDwdInstructionsDialog = ({
  open,
  onOpenChange,
}: GoogleDwdInstructionsDialogProps) => {
  const { mailboxService } = useServices();

  const handleCopyScopes = () => {
    navigator.clipboard.writeText(
      "https://www.googleapis.com/auth/gmail.send, https://www.googleapis.com/auth/userinfo.email, https://www.googleapis.com/auth/userinfo.profile"
    );
    toast.success("Scopes copied to clipboard");
  };

  const handleGoogleProceed = async () => {
    try {
      const response: any = await mailboxService.googleOauthInit();
      if (response?.url) {
        window.location.href = response.url;
      } else {
        toast.error("Failed to initiate Google OAuth. Please try again.");
      }
    } catch (error) {
      console.error("Google OAuth Init Error:", error);
      toast.error("An error occurred during Google authentication.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Google Workspace Setup Required</DialogTitle>
          <DialogDescription>
            Connect Your Google Workspace Mailbox With InboxDoctor.
          </DialogDescription>
        </DialogHeader>
        <div className="pb-4 space-y-3 text-sm text-muted-foreground">
          <div className="flex justify-center">
            <Button
              variant="outline"
              className="flex items-center gap-2 h-10 px-4 w-full max-w-xs"
              onClick={() =>
                window.open("https://youtu.be/vNtEXgmV944", "_blank")
              }
            >
              <PlayCircle className="h-5 w-5" />
              See tutorial video
            </Button>
          </div>

          <p>
            <strong>Steps To Connect Google Workspace Mailbox:</strong>
          </p>
          <ol className="list-decimal list-inside space-y-1 pl-2">
            <li>Click "Proceed to Connect".</li>
            <li>Log in or choose your Gmail account.</li>
            <li>Review the permissions and click "Continue".</li>
            <li>
              Select the desired access (e.g., "Send email on your behalf") and
              click "Continue".
            </li>
            <li>
              Wait for the "Google Workspace connected successfully"
              confirmation.
            </li>
          </ol>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleGoogleProceed}>Proceed to Connect</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Define the MicrosoftConsentInstructionsDialog component
interface MicrosoftConsentInstructionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MicrosoftConsentInstructionsDialog = ({
  open,
  onOpenChange,
}: MicrosoftConsentInstructionsDialogProps) => {
  const [tenantId, setTenantId] = useState("");
  const CLIENT_ID = "8e4b305a-d9b2-403d-ad6a-b0d67f6db330";
  const { mailboxService } = useServices();

  const handleRedirectAdmin = () => {
    if (!tenantId.trim()) {
      toast.error("Please enter a valid Tenant ID.");
      return;
    }
    const guidRegex =
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    if (!guidRegex.test(tenantId.trim())) {
      toast.warning("Tenant ID format looks incorrect, but proceeding anyway.");
    }
    const consentUrl = `https://login.microsoftonline.com/${tenantId.trim()}/adminconsent?client_id=${CLIENT_ID}`;
    window.location.href = consentUrl;
    onOpenChange(false);
  };

  const handleMicrosoftProceed = async () => {
    try {
      const response: any = await mailboxService.microsoftOauthInit();
      if (response?.url) {
        window.location.href = response.url;
      } else {
        toast.error("Failed to initiate Microsoft OAuth. Please try again.");
      }
    } catch (error) {
      console.error("Microsoft OAuth Init Error:", error);
      toast.error("An error occurred during Microsoft authentication.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Microsoft 365 Connection</DialogTitle>
          <DialogDescription>
            Connect your Microsoft account. Admin consent might be required
            first.
          </DialogDescription>
        </DialogHeader>
        <div className="pb-2 space-y-4 text-sm">
          <div className="flex justify-center">
            <Button
              variant="outline"
              className="flex items-center gap-2 h-10 px-4 w-full max-w-xs"
              onClick={() =>
                window.open("https://youtu.be/Ta-TAuZ4-94", "_blank")
              }
            >
              <PlayCircle className="h-5 w-5" />
              See tutorial video
            </Button>
          </div>

          <div>
            <p className="font-medium text-foreground mb-1.5">
              Step 1: Admin Consent (If Needed)
            </p>
            <p className="text-muted-foreground text-xs mb-2">
              If this is the first time anyone from your organization is
              connecting, a Microsoft 365 admin must grant consent.
            </p>
            <ol className="list-decimal list-inside space-y-1 pl-4 text-xs text-muted-foreground mb-3">
              <li>
                Ask your admin to enter the organization's{" "}
                <strong>Tenant ID</strong> below.
              </li>
              <li>
                Admin clicks "Redirect Admin for Consent" and approves the
                request on Microsoft's page.
              </li>
            </ol>
            <label
              htmlFor="tenantId"
              className="block text-xs font-medium text-muted-foreground mb-1"
            >
              Microsoft Tenant ID (for Admin Consent)
            </label>
            <div className="flex items-center gap-2">
              <Input
                id="tenantId"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                placeholder="Enter Tenant ID here"
                className="flex-grow h-9 text-sm"
              />
              <Button
                onClick={handleRedirectAdmin}
                variant="secondary"
                size="sm"
                disabled={!tenantId.trim()}
              >
                Redirect Admin for Consent
              </Button>
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <p className="font-medium text-foreground mb-1.5">
              Step 2: Connect Your Account
            </p>
            <p className="text-muted-foreground text-xs mb-3">
              Once admin consent is granted (or if it was granted previously),
              click below to connect your individual Microsoft account.
            </p>
            <Button onClick={handleMicrosoftProceed} className="w-full h-10">
              Connect My Microsoft Account
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Define the GoogleWorkspaceBulkDialog component
interface GoogleWorkspaceBulkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type WorkspaceUser = { email: string; firstName: string; lastName: string };

const GoogleWorkspaceBulkDialog: React.FC<GoogleWorkspaceBulkDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const [customerId, setCustomerId] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [availableUsers, setAvailableUsers] = useState<
    WorkspaceUser[] | null
  >(null);
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [fetchingMailboxes, setFetchingMailboxes] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [importedAdded, setImportedAdded] = useState<
    Array<{ email: string; mailboxId: string }> | null
  >(null);
  const { mailboxService } = useServices();

  const handleCopyScopes = () => {
    navigator.clipboard.writeText(
      "https://www.googleapis.com/auth/admin.directory.user.readonly, https://www.googleapis.com/auth/gmail.send, https://mail.google.com/"
    );
    toast.success("Scopes copied to clipboard");
  };

  const handleFetchAvailable = async () => {
    if (!customerId.trim()) {
      toast.error("Please enter a Customer ID");
      return;
    }
    if (!adminEmail.trim() || !adminEmail.includes("@")) {
      toast.error("Please enter a valid admin email");
      return;
    }
    setFetchingMailboxes(true);
    setAvailableUsers(null);
    setSelectedEmails(new Set());
    setImportedAdded(null);
    try {
      const response = await mailboxService.googleWorkspaceAvailableMailboxes({
        customerId: customerId.trim(),
        adminEmail: adminEmail.trim(),
      });
      setAvailableUsers(response.users ?? []);
      if (!response.users?.length) {
        toast.info("No available mailboxes found for this workspace.");
      }
    } catch (error) {
      console.error("Google Workspace available mailboxes error:", error);
      toast.error("Failed to fetch available mailboxes,please check the customer id and required scopes are correct");
    } finally {
      setFetchingMailboxes(false);
    }
  };

  const toggleEmail = (email: string) => {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const selectAll = () => {
    if (!availableUsers?.length) return;
    setSelectedEmails(new Set(availableUsers.map((u) => u.email)));
  };

  const selectNone = () => {
    setSelectedEmails(new Set());
  };

  const handleImportSelected = async () => {
    const emails = Array.from(selectedEmails);
    if (!emails.length) {
      toast.error("Please select at least one mailbox to import.");
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await mailboxService.googleWorkspaceImportSelected({
        customerId: customerId.trim(),
        adminEmail: adminEmail.trim(),
        emails,
      });
      setImportedAdded(response.added ?? []);
      const count = response.added?.length ?? 0;
      toast.success(
        count > 0
          ? `Successfully imported ${count} mailbox(es).`
          : "Import completed."
      );
    } catch (error) {
      console.error("Google Workspace import selected error:", error);
      toast.error("Failed to import selected mailboxes");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (openState: boolean) => {
    if (!openState) {
      setAvailableUsers(null);
      setSelectedEmails(new Set());
      setImportedAdded(null);
    }
    onOpenChange(openState);
  };

  const showSelectionPopup =
    !!availableUsers?.length || !!(importedAdded && importedAdded.length > 0);

  const closeSelectionPopup = () => {
    setAvailableUsers(null);
    setSelectedEmails(new Set());
    setImportedAdded(null);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Google Workspace Bulk Import</DialogTitle>
          <DialogDescription>
            Fetch available mailboxes, select which to import, then import
            them.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-center">
          <Button
            variant="outline"
            className="flex items-center gap-2 h-10 px-4 w-full max-w-xs"
            onClick={() =>
              window.open("https://youtu.be/wA4v056GTfI", "_blank")
            }
          >
            <PlayCircle className="h-5 w-5" />
            See tutorial video
          </Button>
        </div>
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>
            <strong>Domain-Wide Delegation Required:</strong>
          </p>
          <p>
            Before proceeding, ensure your Google Workspace administrator has
            granted Domain-Wide Delegation (DWD) access to our application.
          </p>
          <p>
            <strong>Instructions for your Admin:</strong>
          </p>
          <ol className="list-decimal list-inside space-y-1 pl-2">
            <li>Go to the Google Workspace Admin console.</li>
            <li>
              Navigate to Security {">"} Access and data control {">"} API
              controls.
            </li>
            <li>
              Under "Domain wide delegation", click "Manage Domain Wide
              Delegation".
            </li>
            <li>Click "Add new".</li>
            <li>
              Enter our Client ID:{" "}
              <code className="bg-muted p-1 rounded text-xs break-all">
                112673209681796178197
              </code>
            </li>
            <li>
              Enter the required OAuth scopes:{" "}
              <div className="relative mt-1 mb-2">
                <div className="bg-muted p-2 rounded text-xs break-words whitespace-normal">
                  https://www.googleapis.com/auth/admin.directory.user.readonly,
                  https://www.googleapis.com/auth/gmail.send,
                  https://mail.google.com/
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="absolute right-1 top-1 h-6 px-2"
                  onClick={handleCopyScopes}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </li>
            <li>Click "Authorize".</li>
          </ol>
          <p className="mt-4">
            Enter Customer ID and Administrator Email, then fetch available
            mailboxes and select which to import.
          </p>
        </div>
        <div className="grid gap-4">
          <div className="space-y-2">
            <label htmlFor="customerId" className="text-sm font-medium">
              Customer ID
            </label>
            <Input
              id="customerId"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              placeholder="e.g. C01abc123"
            />
            <p className="text-xs text-muted-foreground">
              Your Google Workspace Customer ID can be found in the Admin
              Console.
            </p>
          </div>
          <div className="space-y-2">
            <label htmlFor="adminEmail" className="text-sm font-medium">
              Administrator Email
            </label>
            <Input
              id="adminEmail"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="admin@yourdomain.com"
              type="email"
            />
            <p className="text-xs text-muted-foreground">
              Email address of an administrator with access to the directory.
            </p>
          </div>
        </div>

        {availableUsers === null && !fetchingMailboxes && (
          <DialogFooter>
            <Button variant="outline" onClick={() => handleClose(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleFetchAvailable}
              disabled={!customerId.trim() || !adminEmail.trim()}
            >
              Fetch available mailboxes
            </Button>
          </DialogFooter>
        )}

        {fetchingMailboxes && (
          <div className="flex items-center justify-center gap-2 py-4">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm text-muted-foreground">
              Fetching available mailboxes...
            </span>
          </div>
        )}

        {availableUsers && availableUsers.length === 0 && !importedAdded && (
          <DialogFooter>
            <p className="text-sm text-muted-foreground w-full">
              No available mailboxes found.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setAvailableUsers(null);
              }}
            >
              Back
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>

      {/* Popup: select mailboxes or show success */}
      <Dialog open={showSelectionPopup} onOpenChange={(isOpen) => !isOpen && closeSelectionPopup()}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          {importedAdded && importedAdded.length > 0 ? (
            <>
              <DialogHeader>
                <DialogTitle>Import complete</DialogTitle>
                <DialogDescription>
                  Successfully imported {importedAdded.length} mailbox(es).
                </DialogDescription>
              </DialogHeader>
              <ul className="max-h-[280px] overflow-y-auto rounded-md border p-2 space-y-1.5 text-sm">
                {importedAdded.map(({ email, mailboxId }) => (
                  <li key={mailboxId} className="flex justify-between gap-2">
                    <span className="truncate">{email}</span>
                    <span className="text-muted-foreground shrink-0 font-mono text-xs">
                      {mailboxId}
                    </span>
                  </li>
                ))}
              </ul>
              <DialogFooter>
                <Button variant="outline" onClick={closeSelectionPopup}>
                  Close
                </Button>
                <Button
                  onClick={() => {
                    setImportedAdded(null);
                    setSelectedEmails(new Set());
                  }}
                >
                  Import more
                </Button>
              </DialogFooter>
            </>
          ) : (
            availableUsers &&
            availableUsers.length > 0 && (
              <>
                <DialogHeader>
                  <DialogTitle>Select mailboxes to import</DialogTitle>
                  <DialogDescription>
                    Choose which users to import (e.g. 4–5 or any subset).
                  </DialogDescription>
                </DialogHeader>
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={selectAll}
                    >
                      Select all
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={selectNone}
                    >
                      Select none
                    </Button>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {selectedEmails.size} selected
                  </span>
                </div>
                <div className="max-h-[280px] overflow-y-auto rounded-md border p-2 space-y-1.5">
                  {availableUsers.map((user) => (
                    <label
                      key={user.email}
                      className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-muted/50"
                    >
                      <Checkbox
                        checked={selectedEmails.has(user.email)}
                        onCheckedChange={() => toggleEmail(user.email)}
                      />
                      <span className="text-sm truncate">
                        {user.firstName} {user.lastName}{" "}
                        <span className="text-muted-foreground">
                          ({user.email})
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={closeSelectionPopup}>
                    Back
                  </Button>
                  <Button
                    onClick={handleImportSelected}
                    disabled={isSubmitting || selectedEmails.size === 0}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      `Import ${selectedEmails.size} selected`
                    )}
                  </Button>
                </DialogFooter>
              </>
            )
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
